import type { Metafield } from "../types/catalog";

/** A product's review rating and how many reviews it rests on. */
export interface ReviewSummary {
  /** `4.8`. */
  rating: number;
  /** The top of the scale, `5` on every product in this store. */
  scaleMax: number;
  count: number;
}

function find(metafields: Metafield[], key: string): Metafield | undefined {
  return metafields.find(
    (field) => field.namespace === "reviews" && field.key === key,
  );
}

/**
 * Read `reviews.rating` and `reviews.rating_count` — the two metafields
 * FR-CAT-5 (amended 2026-09-13) shows.
 *
 * Here rather than in a screen because three places read them: the drawer and
 * cards draw stars, and both adapters filter and sort on the figures. One
 * parser means "rated 4.8" cannot mean one thing to the filter and another to
 * the stars beside it.
 *
 * A `rating` value is JSON — `{"scale_min","scale_max","value"}` — as Shopify
 * documents the type. Anything unparseable is no rating rather than zero,
 * which would sort a product with a broken metafield below every real one.
 */
export function reviewSummary(metafields: Metafield[]): ReviewSummary | null {
  const ratingField = find(metafields, "rating");
  if (!ratingField) return null;

  try {
    const parsed = JSON.parse(ratingField.value) as {
      value?: string;
      scale_max?: string;
    };
    const rating = Number(parsed.value);
    const scaleMax = Number(parsed.scale_max ?? 5);
    if (!Number.isFinite(rating) || !(scaleMax > 0)) return null;

    const count = Number(find(metafields, "rating_count")?.value ?? 0);
    return {
      rating,
      scaleMax,
      count: Number.isFinite(count) ? count : 0,
    };
  } catch {
    return null;
  }
}
